import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

interface V2ModeStatus {
  isV2: boolean;
}

const getV2ModeStatus = async (): Promise<V2ModeStatus> => {
  const response = await baseAPI.get<V2ModeStatus>('/v2-mode/status');
  return response.data;
};

const enableV2Mode = async (): Promise<{ message: string; isV2: boolean }> => {
  const response = await baseAPI.post<{ message: string; isV2: boolean }>(
    '/v2-mode/enable',
  );
  return response.data;
};

const disableV2Mode = async (): Promise<{ message: string; isV2: boolean }> => {
  const response = await baseAPI.post<{ message: string; isV2: boolean }>(
    '/v2-mode/disable',
  );
  return response.data;
};

export const useGetV2ModeStatus = () => {
  return useQuery({
    queryKey: [KEYS.V2_MODE_STATUS],
    queryFn: getV2ModeStatus,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
};

export const useEnableV2Mode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enableV2Mode,
    onSuccess: (data) => {
      toast.success(data.message || 'V2 enrollment mode enabled');
      void queryClient.invalidateQueries({ queryKey: [KEYS.V2_MODE_STATUS] });
      void queryClient.invalidateQueries({ queryKey: [KEYS.STUDIO_DETAILS] });
    },
    onError: (error: unknown) => {
      const message =
        error &&
        typeof error === 'object' &&
        'response' in error &&
        error.response &&
        typeof error.response === 'object' &&
        'data' in error.response &&
        error.response.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data &&
        typeof error.response.data.message === 'string'
          ? error.response.data.message
          : 'Failed to enable V2 mode';
      toast.error(message);
    },
  });
};

export const useDisableV2Mode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disableV2Mode,
    onSuccess: (data) => {
      toast.success(data.message || 'V2 enrollment mode disabled');
      void queryClient.invalidateQueries({ queryKey: [KEYS.V2_MODE_STATUS] });
      void queryClient.invalidateQueries({ queryKey: [KEYS.STUDIO_DETAILS] });
    },
    onError: () => {
      toast.error('Failed to disable V2 mode');
    },
  });
};
